import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/*
 * ATD fork de header-menu.js (core Horizon). NO editar el core.
 * Única diferencia vs. el original: un "hover-intent" en deactivate() para el panel
 * de overflow ("Más"). Bug original: al salir del li[slot="more"] DE COSTADO hacia un
 * link vecino, relatedTarget no cae dentro de [slot="overflow"], así que isMoving... era
 * false y el panel se cerraba antes de que el cursor llegara al item de overflow
 * (ENCUÉNTRANOS), que se pinta en grid-area:left (lejos de "Más"). El guard difiere el
 * cierre con un timeout corto cuando el overflow está expandido y el cursor sigue dentro
 * del propio header-menu, dándole tiempo al viaje diagonal. Todo lo demás es idéntico.
 */

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * ATD: hover-intent timer id para diferir el cierre del overflow mientras el cursor
   * viaja desde "Más" hacia el item de overflow (ENCUÉNTRANOS).
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #overflowCloseTimer = null;

  /**
   * ATD: ventana de tolerancia (ms) para el viaje del cursor dentro del header-menu
   * antes de cerrar el panel de overflow.
   */
  #overflowCloseDelay = 220;

  /**
   * ATD: timer id para diferir la activación de un item NORMAL cuando el overflow está
   * expandido. Es hover-intent dirigido por MOVIMIENTO: se re-arma en cada pointermove, así
   * que sólo vence cuando el cursor se queda QUIETO sobre el item (selección deliberada). Si
   * el cursor sigue viajando (hacia ENCUÉNTRANOS), nunca vence y el overflow no se colapsa.
   * @type {ReturnType<typeof setTimeout> | null}
   */
  #pendingActivateTimer = null;

  /**
   * ATD: el evento de activación normal diferido pendiente de aplicar (si lo hay).
   * @type {PointerEvent | FocusEvent | null}
   */
  #pendingActivateEvent = null;

  /**
   * ATD: última posición conocida del puntero (clientX/clientY).
   * @type {{ x: number, y: number } | null}
   */
  #lastPointer = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    // ATD: rastrear el puntero para el hover-intent del overflow.
    this.addEventListener('pointermove', this.#pointerTracker);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.removeEventListener('pointermove', this.#pointerTracker);
    this.#clearOverflowCloseTimer();
    this.#clearPendingActivate();
    this.#cleanupMutationObserver();
  }

  /**
   * ATD: registra la posición del puntero y, mientras se MUEVE dentro del header con el
   * overflow expandido, re-arma los timers de hover-intent: la activación normal pendiente
   * se pospone (sólo se aplica cuando el cursor se queda quieto) y el cierre del overflow se
   * re-agenda (tolera el viaje completo hacia ENCUÉNTRANOS).
   * @param {PointerEvent} event
   */
  #pointerTracker = (event) => {
    this.#lastPointer = { x: event.clientX, y: event.clientY };

    if (this.dataset.overflowExpanded !== 'true') return;

    // El cursor sigue en movimiento: re-arma la activación normal diferida (si hay una)
    // y el cierre del overflow, de modo que ambos sólo venzan cuando el cursor se detenga.
    if (this.#pendingActivateEvent) {
      this.#armPendingActivate();
    }
    if (this.#overflowCloseTimer !== null) {
      this.#scheduleOverflowClose();
    }
  };

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);


  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    // ATD: cualquier (re)activación cancela un cierre de overflow pendiente. Cubre el
    // caso de entrar al item de overflow (o a "Más" de nuevo) durante el viaje diagonal.
    this.#clearOverflowCloseTimer();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item || item == this.#state.activeItem) {
      // Re-hover del item ya activo: nada que diferir, pero sí cancelar cualquier
      // activación normal pendiente (el usuario se quedó donde ya estaba).
      this.#clearPendingActivate();
      return;
    }

    const isDefaultSlot = event.target.slot === '';

    // ATD: hover-intent al ENTRAR. Si el overflow ("Más") está expandido y el cursor
    // toca un item NORMAL (default slot) que NO es el activo, NO lo activamos de inmediato
    // —eso colapsaría el panel de overflow durante el viaje diagonal hacia ENCUÉNTRANOS—.
    // Lo diferimos: si el cursor se queda sobre ese item, se activa normal; si sigue de
    // paso (hacia el overflow), un nuevo enter/leave cancela el pendiente y el overflow
    // sobrevive. El item de overflow y "Más" se activan siempre al instante.
    const overflowExpanded = this.dataset.overflowExpanded === 'true';
    if (overflowExpanded && isDefaultSlot) {
      // Diferir: guardamos el evento y armamos el timer de hover-intent. #pointerTracker
      // lo re-arma en cada movimiento, así sólo se aplica si el cursor se queda quieto aquí.
      this.#pendingActivateEvent = event;
      this.#armPendingActivate();
      return;
    }

    // Activación inmediata (caso original): cualquier pendiente queda obsoleto.
    this.#clearPendingActivate();
    this.#applyActivate(event);
  };

  /**
   * ATD: (re)arma el timer de la activación normal diferida. Se llama al entrar a un item
   * normal con overflow abierto, y en cada pointermove para posponerla mientras haya viaje.
   */
  #armPendingActivate = () => {
    if (!this.#pendingActivateEvent) return;
    if (this.#pendingActivateTimer !== null) clearTimeout(this.#pendingActivateTimer);
    this.#pendingActivateTimer = setTimeout(() => {
      this.#pendingActivateTimer = null;
      const pending = this.#pendingActivateEvent;
      this.#pendingActivateEvent = null;
      if (pending) this.#applyActivate(pending);
    }, this.#overflowCloseDelay);
  };

  /**
   * ATD: cuerpo original de activate (Horizon). Aplica la activación del item ya resuelto.
   * @param {PointerEvent | FocusEvent} event
   */
  #applyActivate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // ATD: al aplicar una activación (inmediata o diferida), ningún cierre de overflow
    // diferido debe quedar pendiente para no pisar el nuevo estado.
    this.#clearOverflowCloseTimer();

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // ATD: hover-intent SOLO para el panel de overflow expandido. Cuando "Más" está
    // abierto y el cursor sale de un li hacia otro nodo que sigue DENTRO del header-menu
    // (p.ej. un link vecino, en el viaje diagonal hacia ENCUÉNTRANOS), no cerramos de
    // inmediato: diferimos con un timeout corto. Si el cursor llega al item de overflow,
    // activate() (o el :hover que checa #deactivate) cancela el cierre; si no, cierra.
    // No aplica a hover normal de items con submenu (overflow no expandido), ni a salidas
    // del header (relatedTarget fuera de this), ni al drawer móvil (otro componente).
    const overflowExpanded = this.dataset.overflowExpanded === 'true';
    const isMovingWithinHeaderMenu = event.relatedTarget instanceof Node && this.contains(event.relatedTarget);

    if (overflowExpanded && isMovingWithinHeaderMenu) {
      this.#scheduleOverflowClose();
      return;
    }

    this.#deactivate();
  }

  /**
   * ATD: agenda (o re-agenda) el cierre diferido del overflow. Al vencer, si el puntero
   * sigue dentro de la zona del header (header-component o el panel de overflow), re-agenda
   * para tolerar un viaje diagonal largo hacia ENCUÉNTRANOS; si el puntero ya salió de esa
   * zona, cierra de verdad. Salir del header dispara la rama directa de #deactivate, no esto.
   */
  #scheduleOverflowClose = () => {
    this.#clearOverflowCloseTimer();
    this.#overflowCloseTimer = setTimeout(() => {
      this.#overflowCloseTimer = null;
      if (this.dataset.overflowExpanded === 'true' && this.#pointerInHeaderZone()) {
        // El puntero sigue viajando dentro del header: dale más tiempo.
        this.#scheduleOverflowClose();
        return;
      }
      this.#deactivate();
    }, this.#overflowCloseDelay);
  };

  /**
   * ATD: ¿el último puntero conocido cae dentro del header-component o del panel de overflow?
   * @returns {boolean}
   */
  #pointerInHeaderZone = () => {
    const p = this.#lastPointer;
    if (!p) return false;
    const within = (/** @type {Element | null | undefined} */ el) => {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      return p.x >= r.left && p.x <= r.right && p.y >= r.top && p.y <= r.bottom;
    };
    return within(this.headerComponent) || within(this.overflowMenu);
  };

  /**
   * ATD: cancela un cierre de overflow diferido pendiente.
   */
  #clearOverflowCloseTimer = () => {
    if (this.#overflowCloseTimer !== null) {
      clearTimeout(this.#overflowCloseTimer);
      this.#overflowCloseTimer = null;
    }
  };

  /**
   * ATD: cancela una activación de item normal diferida pendiente.
   */
  #clearPendingActivate = () => {
    if (this.#pendingActivateTimer !== null) {
      clearTimeout(this.#pendingActivateTimer);
      this.#pendingActivateTimer = null;
    }
    this.#pendingActivateEvent = null;
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    // ATD: si llegamos a cerrar de verdad, no dejar timers de hover-intent colgando.
    this.#clearOverflowCloseTimer();
    this.#clearPendingActivate();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    }

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
